import { type FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { KTCard, KTCardBody, KTIcon } from "../../../../_metronic/helpers";
import { PageTitle } from "../../../../_metronic/layout/core";
import { DataTable } from "../../../../_metronic/partials/common/DataTable";
import { useDecryptedRole } from "../../../../utility/roleIdDecryption";
import { useAuth } from "../../../modules/auth";
import { useDeleteEmployee, useEmployees } from "../hooks/useRegistration";

const RegistrationPage: FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedRole, setSelectedRole] = useState<any>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const { currentUser } = useAuth();
	const decryptedRoleId = useDecryptedRole();
	const role_id = decryptedRoleId ?? (Number(currentUser?.role_id) || null);

	const isStateOfficerMode = location.pathname.includes("state-officer") || location.pathname.includes("state");

	const roleOptions = useMemo(
		() => [
			{ value: 3, label: "DRP" },
			{ value: 4, label: "BRP" },
		],
		[],
	);

	const roleParam = searchParams.get("role");
	const districtParam = searchParams.get("district") || searchParams.get("district_id");
	const districtNameParam = searchParams.get("districtName");

	useEffect(() => {
		if (roleParam) {
			const found = roleOptions.find(
				(r: any) =>
					String(r.value) === roleParam ||
					r.label.toLowerCase() === roleParam.toLowerCase(),
			);
			if (found) {
				setSelectedRole(found);
			}
		} else if (!roleParam) {
			setSelectedRole(null);
		}
	}, [roleParam, roleOptions]);

	const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

	useEffect(() => {
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, [selectedRole, districtParam]);

	const effectiveTypeId = isStateOfficerMode
		? 2
		: selectedRole?.value
			? selectedRole.value
			: "3,4,5";

	const {
		employees: rawData,
		isLoading: loading,
		totalCount: rawTotalCount,
		totalPages: rawTotalPages,
	} = useEmployees({
		type_id: effectiveTypeId,
		district_id: districtParam || undefined,
		page: pagination.pageIndex + 1,
		limit: pagination.pageSize,
	});

	const data = useMemo(() => {
		if (!rawData) return [];
		return rawData;
	}, [rawData]);

	const displayTotalCount = rawTotalCount ?? data.length;
	const displayPageCount = rawTotalPages ?? 1;
	const deleteEmployeeMutation = useDeleteEmployee();

	const handleEditClick = (id: number) => {
		navigate(`/registration/edit/${id}`);
	};

	const handleViewClick = (id: number) => {
		navigate(`/registration/view/${id}`);
	};

	const handleComplaintClick = (id: number) => {
		navigate(`/registration/complaint/${id}`);
	};

	const handleDeleteClick = async (id: number) => {
		const result = await Swal.fire({
			title: "Are you sure?",
			text: "You won't be able to revert this!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#131c5d",
			cancelButtonColor: "#f57c00",
			confirmButtonText: "Yes, delete it!",
		});

		if (result.isConfirmed) {
			deleteEmployeeMutation.mutate(id, {
				onSuccess: () => {
					Swal.fire("Deleted!", "Employee has been deleted.", "success");
				},
				onError: (error) => {
					console.error("Failed to delete employee", error);
					Swal.fire("Error!", "Failed to delete employee.", "error");
				},
			});
		}
	};

	const showAddButton = useMemo(() => {
		if (isStateOfficerMode) {
			return false; // Removed Add Profile Details button for State Officer Registration menu
		}

		if (role_id === 3 || role_id === 4) {
			if (!data || data.length === 0) return true;
			// Hide if current user has ANY record in the list (In-Progress or Completed)
			const hasExistingRecord = data.some(
				(e: any) =>
					e.employee_code === currentUser?.employee_code ||
					e.mobile_no === currentUser?.mobile,
			);
			return !hasExistingRecord;
		}

		return false;
	}, [data, role_id, currentUser, isStateOfficerMode]);

	const columns = useMemo(
		() => [
			{
				header: "S.No.",
				cell: (info: any) => {
					const pageIndex = info.table.getState().pagination.pageIndex;
					const pageSize = info.table.getState().pagination.pageSize;
					return pageIndex * pageSize + info.row.index + 1;
				},
				meta: { align: "center" },
			},
			{
				accessorKey: "employee_code",
				header: "Emp Code",
				meta: { align: "start" },
			},
			{
				accessorKey: "name",
				header: "Name",
				meta: { align: "start" },
			},
			{
				accessorKey: "mobile_no",
				header: "Mobile",
				meta: { align: "start" },
			},
			{
				accessorKey: "email",
				header: "Email",
				meta: { align: "start" },
			},
			...(isStateOfficerMode
				? [
						{
							accessorKey: "state_name",
							header: "State",
							cell: (info: any) => info.getValue() || "Tamil Nadu",
							meta: { align: "start" },
						},
					]
				: [
						{
							accessorKey: "district_name",
							header: "District",
							meta: { align: "start" },
						},
						{
							accessorKey: "block_name",
							header: "Block",
							meta: { align: "start" },
						},
					]),
			{
				accessorKey: "status",
				header: "Status",
				cell: (info: any) => {
					const status = info.getValue() as string;
					const isCompleted = status?.toLowerCase() === "completed";
					return (
						<span
							className={`badge badge-light-${isCompleted ? "success" : "warning"} fw-bold p-3 fs-8`}
						>
							{status || "N/A"}
						</span>
					);
				},
				meta: { align: "center" },
			},

			{
				id: "actions",
				header: "Actions",
				cell: (info: any) => {
					const step = info.row.original.step;
					const status = info.row.original.status;
					const isCompleted = status?.toLowerCase() === "completed" || step === 5;
					const empCode = info.row.original.employee_code || info.row.original.username;
					const isOwnProfile = Boolean(
						empCode &&
							(currentUser?.employee_code === empCode || currentUser?.username === empCode),
					);

					const showCompleteProfile = isStateOfficerMode && !isCompleted && role_id === 1;
					const showEdit = isStateOfficerMode
						? isCompleted && (role_id === 1 || isOwnProfile)
						: role_id === 1 || isOwnProfile;
					const showView = true;
					const showComplaint = role_id === 1;

					return (
						<div className="d-flex align-items-center justify-content-center gap-2">
							{showCompleteProfile && (
								<button
									type="button"
									className="btn btn-sm btn-primary py-1 px-3 fs-8 fw-bold text-nowrap"
									onClick={() => handleEditClick(info.row.original.id)}
									title="Complete Profile"
								>
									Complete Profile
								</button>
							)}
							{showEdit && (
								<button
									type="button"
									className="btn btn-icon btn-light-primary btn-sm me-1"
									onClick={() => handleEditClick(info.row.original.id)}
									title="Edit"
								>
									<KTIcon iconName="pencil" className="fs-3" />
								</button>
							)}
							{showView && (
								<button
									type="button"
									className="btn btn-icon btn-light-success btn-sm me-1"
									onClick={() => handleViewClick(info.row.original.id)}
									title="View"
								>
									<KTIcon iconName="eye" className="fs-3" />
								</button>
							)}
							{showComplaint && (
								<button
									type="button"
									className="btn btn-icon btn-light-warning btn-sm"
									title="Complaint"
									onClick={() => handleComplaintClick(info.row.original.id)}
								>
									<KTIcon iconName="complaint" className="fs-3" />
								</button>
							)}
						</div>
					);
				},
				meta: { align: "center" },
			},
		],
		[
			handleEditClick,
			handleViewClick,
			handleDeleteClick,
			handleComplaintClick,
			role_id,
		],
	);

	return (
		<>
			<PageTitle breadcrumbs={[]}>
				{isStateOfficerMode ? "State Officer Registration" : "Staff Registration"}
			</PageTitle>

			{(!isStateOfficerMode && (role_id === 1 || role_id === 2) || showAddButton || selectedRole || districtNameParam || districtParam) && (
				<KTCard className="mb-3 glass-card">
					<KTCardBody className="py-3 px-5">
						<div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-4">
							{!isStateOfficerMode && (role_id === 1 || role_id === 2) && (
								<div className="d-flex align-items-center gap-3 w-100 w-md-auto">
									<span className="fs-7 fw-bold text-gray-700 text-nowrap">
										Filter by Role:
									</span>
									<div style={{ minWidth: "220px" }}>
										<Select
											options={roleOptions}
											value={selectedRole}
											onChange={(opt: any) => {
												setSelectedRole(opt);
												if (opt) {
													setSearchParams({ role: String(opt.value) });
												} else {
													setSearchParams({});
												}
											}}
											isClearable
											placeholder="All Roles"
											classNamePrefix="react-select"
											menuPortalTarget={typeof document !== "undefined" ? document.body : null}
											styles={{
												menuPortal: (base) => ({ ...base, zIndex: 9999 }),
											}}
										/>
									</div>
								</div>
							)}

							<div className="card-toolbar w-100 w-md-auto ms-auto">
								{showAddButton && (
									<button
										type="button"
										className="btn btn-sm btn-primary d-flex align-items-center justify-content-center gap-2 w-100 w-md-auto"
										onClick={() =>
											navigate(
												isStateOfficerMode
													? "/registration/add/2"
													: selectedRole
														? `/registration/add/${selectedRole.value}`
														: "/registration/add",
											)
										}
									>
										<KTIcon iconName="plus" className="fs-2" />
										Add Profile Details
									</button>
								)}
							</div>
						</div>

						{(selectedRole || districtNameParam || districtParam) && (
							<div className="mt-4 p-3 rounded bg-light-primary border border-primary border-opacity-25 d-flex flex-wrap align-items-center justify-content-between gap-2">
								<div className="d-flex align-items-center gap-2 flex-wrap">
									<span className="badge badge-primary fs-8">Active Filters</span>
									{selectedRole && (
										<span className="fs-7 fw-bold text-primary">
											Role: <strong>{selectedRole.label}</strong>
										</span>
									)}
									{selectedRole && (districtNameParam || districtParam) && (
										<span className="text-gray-400">•</span>
									)}
									{(districtNameParam || districtParam) && (
										<span className="fs-7 fw-bold text-primary">
											District: <strong>{districtNameParam || `ID: ${districtParam}`}</strong>
										</span>
									)}
									<span className="fs-7 text-gray-600">({displayTotalCount || 0} records)</span>
								</div>
								<button
									type="button"
									className="btn btn-sm btn-light-primary py-1 px-3 fs-8 fw-bold"
									onClick={() => {
										setSelectedRole(null);
										setSearchParams({});
									}}
								>
									Clear Filters ×
								</button>
							</div>
						)}
					</KTCardBody>
				</KTCard>
			)}

			<KTCard className="glass-card">
				<KTCardBody className="py-4 w-100">
					<DataTable
						columns={columns as any}
						data={data}
						isLoading={loading}
						manualPagination={true}
						totalCount={displayTotalCount}
						pageCount={displayPageCount}
						onPaginationChange={setPagination}
						currentPageIndex={pagination.pageIndex}
						currentPageSize={pagination.pageSize}
					/>
				</KTCardBody>
			</KTCard>
		</>
	);
};

export default RegistrationPage;
